import { useEffect, useMemo, useState } from 'react';
import {
  CreateInsuranceRequest,
  Plan,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { PersonHelpers } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { SchemaInsuranceVerificationAdapterServiceV1 } from '@frontend/schema';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Button,
  ContentLoader,
  DropdownField,
  Heading,
  Text,
  Tray,
  useAlert,
  useFormField,
} from '@frontend/design-system';
import { useInsuranceType } from '../../providers/insurance-type-provider';
import { usePersonInfoStore } from '../../providers/person-info-provider';
import { writebackModalTrackingIds } from '../../tracking-ids';
import { sanitizeText } from '../../utils/utils';

interface Props {
  onClose: () => void;
}

export const WriteBack = ({ onClose }: Props) => {
  const { t } = useTranslation('insuranceDetails');
  const { insuranceType } = useInsuranceType();
  const alert = useAlert();
  const { personInfo, locationId } = usePersonInfoStore();
  const [plan, setSelectedPlan] = useState<Plan>();

  const patientName = PersonHelpers.getFullName(personInfo);

  const { invalidateGetInsuranceByPersonIdQuery } = InsuranceDetailQueries.useInvalidateQueries();

  //This will fetch the latest insurance request details which will be used for filtering insurance plans
  const { data: insuranceData, isFetching: isFetchingInsuranceDetails } =
    InsuranceDetailQueries.useGetCachedDentalCoverageInfoByPersonId(
      personInfo?.PersonID || '',
      locationId || '',
      insuranceType
    );

  const {
    data: insurancePlansResponse,
    isFetching: isFetchingInsurancePlans,
    refetch: refetchInsurancePlans,
  } = InsuranceDetailQueries.useGetInsurancePlans(personInfo?.SourceID || '', locationId!);

  const insuranceRequestDetails = insuranceData?.insuranceRequestDetails;
  const isFetching = isFetchingInsuranceDetails || isFetchingInsurancePlans;

  const insurancePlans = useMemo(() => {
    const gName = sanitizeText(insuranceRequestDetails?.groupName);
    const gNumber = sanitizeText(insuranceRequestDetails?.groupNumber);
    const hasGroupNameOrNumber = Boolean(gName || gNumber);

    return (
      insurancePlansResponse
        ?.filter((plan) => !hasGroupNameOrNumber || plan.gNam === gName || plan.gNum === gNumber)
        .map((plan) => ({
          value: plan.plID ?? '',
          label: [plan.cNam, plan.gNam, plan.gNum].reduce((acc, item) => (item ? `${acc} | ${item}` : acc), '') ?? '',
        })) ?? []
    );
  }, [isFetching, insurancePlansResponse, insuranceRequestDetails?.groupName, insuranceRequestDetails?.groupNumber]);

  const insurancePlanDropdownField = useFormField({ type: 'dropdown', value: insurancePlans[0]?.value ?? '' }, [
    insurancePlans,
  ]);

  useEffect(() => {
    const selectedPlan = insurancePlansResponse?.find((plan) => plan.plID === insurancePlanDropdownField.value);
    setSelectedPlan(selectedPlan);
  }, [insurancePlanDropdownField.value, insurancePlansResponse]);

  const { mutate: writebackInsurance, isLoading: isDoingWriteback } = useMutation({
    mutationFn: (req: CreateInsuranceRequest) => SchemaInsuranceVerificationAdapterServiceV1.CreateInsurance(req),
    onSuccess: () => {
      alert.success(t('Writeback Successful! The updated data will be available in your PMS soon'));
      invalidateGetInsuranceByPersonIdQuery(personInfo?.PersonID ?? '', personInfo?.SourceID ?? '');
    },
    onError: () => {
      alert.error(t('Something went wrong during writeback process. Please try again!'));
    },
  });

  const handleSaveClick = () => {
    if (!Boolean(plan?.plID)) {
      onClose();
      return;
    }

    writebackInsurance({
      personId: personInfo?.PersonID || '',
      sourceId: personInfo?.SourceID,
      companyName: plan?.cNam ?? '',
      groupName: plan?.gNam ?? '',
      planId: plan?.plID ?? '',
      memberId: insuranceRequestDetails?.memberId || '',
      relationship: insuranceRequestDetails?.relationship,
      createdBy: patientName,
    });
  };

  const hasInsurancePlans = !isFetching && insurancePlans.length;

  if (isFetching) {
    return <ContentLoader show={isFetching} />;
  }
  return (
    <div css={{ display: 'flex', height: '100%', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div>
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Update Patient Insurance')}
        </Heading>
        <Text color='light' size='medium'>
          {t(
            'To continue, please add the Group Name and Group Number for this patient’s insurance in your PMS or EHR.'
          )}
        </Text>

        <Text color='light' size='medium' css={{ marginBottom: theme.spacing(2) }}>
          {t('Once completed, tap Refetch to load the updated insurance details.')}
        </Text>
        {hasInsurancePlans ? (
          <>
            <DropdownField
              name='insurance-plan'
              label={t('Insurance Plan')}
              placeholder={t('Select Insurance Plan')}
              {...insurancePlanDropdownField}
              css={{ margin: theme.spacing(2, 0) }}
              data-tracking-id={writebackModalTrackingIds.insurancePlanDropdown}
            >
              {(insurancePlans || []).map((plan) => (
                <DropdownField.Option key={plan.value} value={plan.value}>
                  {plan.label}
                </DropdownField.Option>
              ))}
            </DropdownField>
            <BannerNotification
              status='info'
              message={t(
                'If the correct insurance plan isn’t listed in the dropdown, add it in your PMS/EHR. It will appear here once added.'
              )}
            />
          </>
        ) : (
          <BannerNotification message={t('No Plan configured in the PMS')} status='error' />
        )}

        <Button
          iconName='sync-small'
          variant='primary'
          size='small'
          onClick={() => refetchInsurancePlans()}
          css={{ width: 'auto', marginTop: theme.spacing(2) }}
          trackingId={writebackModalTrackingIds.refetchInsurancePlansButton}
        >
          {t('Refetch')}
        </Button>
      </div>
      <div css={{ marginBottom: theme.spacing(2), marginRight: theme.spacing(2) }}>
        <Tray.Actions
          disabledPrimary={isDoingWriteback || isFetching}
          onSecondaryClick={onClose}
          primaryLabel={t('Update')}
          onPrimaryClick={handleSaveClick}
          primaryTrackingId={writebackModalTrackingIds.writebackModalUpdateButton}
          secondaryTrackingId={writebackModalTrackingIds.writebackModalCloseButton}
        />
      </div>
    </div>
  );
};
